import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Box,
  TextField,
  MenuItem,
  Grid,
  LinearProgress,
  Chip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import PeopleIcon from "@material-ui/icons/People";
import ScheduleIcon from "@material-ui/icons/Schedule";
import { getCampaignShippingEstimations } from "../../services/campaignShippingReports.api";
import toastError from "../../errors/toastError";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const useStyles = makeStyles((theme) => ({
  panel: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1)
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(2)
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1.5)
  },
  infoIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main
  },
  infoLabel: {
    fontWeight: 500,
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  infoValue: {
    fontWeight: 600,
    color: theme.palette.text.primary
  },
  progressContainer: {
    marginTop: theme.spacing(2)
  },
  progressBar: {
    height: 10,
    borderRadius: 5
  },
  statusChip: {
    fontWeight: 600
  }
}));

const EstimationsPanel = ({ campaigns, selectedCampaignId, setSelectedCampaignId }) => {
  const classes = useStyles();
  const [estimations, setEstimations] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedCampaignId) {
      setEstimations(null);
      return;
    }

    const loadEstimations = async () => {
      setLoading(true);
      try {
        const { data } = await getCampaignShippingEstimations(selectedCampaignId);
        setEstimations(data);
      } catch (err) {
        console.error("Error loading estimations:", err);
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    loadEstimations();

    // Actualizar cada 10 segundos si la campaña está en curso
    const interval = setInterval(() => {
      loadEstimations();
    }, 10000);

    return () => clearInterval(interval);
  }, [selectedCampaignId]);

  const getStatusColor = (status) => {
    switch (status) {
      case "EM_ANDAMENTO":
        return "#ff9800";
      case "FINALIZADA":
        return "#4caf50";
      case "CANCELADA":
        return "#f44336";
      case "PROGRAMADA":
        return "#2196f3";
      default:
        return "#9e9e9e";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "EM_ANDAMENTO":
        return "En Curso";
      case "FINALIZADA":
        return "Finalizada";
      case "CANCELADA":
        return "Cancelada";
      case "PROGRAMADA":
        return "Programada";
      default:
        return status;
    }
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}min`;
  };

  return (
    <Paper className={classes.panel}>
      <Typography variant="h6" className={classes.title}>
        <span role="img" aria-label="reloj">⏱️</span> Estimaciones de Envío
      </Typography>

      <Box mb={3}>
        <TextField
          select
          label="Seleccionar Campaña"
          value={selectedCampaignId}
          onChange={(e) => setSelectedCampaignId(e.target.value)}
          fullWidth
          variant="outlined"
        >
          <MenuItem value="">Seleccione una campaña</MenuItem>
          {campaigns.map((campaign) => (
            <MenuItem key={campaign.id} value={campaign.id}>
              {campaign.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {loading && <LinearProgress />}

      {estimations && !loading && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box className={classes.infoRow}>
              <PeopleIcon className={classes.infoIcon} />
              <Typography className={classes.infoLabel}>Total Contactos:</Typography>
              <Typography className={classes.infoValue}>
                {estimations.totalContacts}
              </Typography>
            </Box>

            <Box className={classes.infoRow}>
              <AccessTimeIcon className={classes.infoIcon} />
              <Typography className={classes.infoLabel}>Intervalo:</Typography>
              <Typography className={classes.infoValue}>
                {estimations.messageInterval} seg
              </Typography>
            </Box>

            <Box className={classes.infoRow}>
              <ScheduleIcon className={classes.infoIcon} />
              <Typography className={classes.infoLabel}>Intervalo Mayor Cada:</Typography>
              <Typography className={classes.infoValue}>
                {estimations.longerIntervalAfter} mensajes ({estimations.greaterInterval} seg)
              </Typography>
            </Box>

            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Estado:</Typography>
              <Chip
                label={getStatusLabel(estimations.status)}
                size="small"
                className={classes.statusChip}
                style={{
                  backgroundColor: getStatusColor(estimations.status),
                  color: "white"
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box className={classes.infoRow}>
              <Typography className={classes.infoLabel}>Duración Estimada:</Typography>
              <Typography className={classes.infoValue}>
                {formatDuration(estimations.estimatedDuration)}
              </Typography>
            </Box>

            {estimations.estimatedStart && (
              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>Inicio:</Typography>
                <Typography className={classes.infoValue}>
                  {format(new Date(estimations.estimatedStart), "dd/MM/yyyy HH:mm", {
                    locale: es
                  })}
                </Typography>
              </Box>
            )}

            {estimations.estimatedEnd && (
              <Box className={classes.infoRow}>
                <Typography className={classes.infoLabel}>Fin Estimado:</Typography>
                <Typography className={classes.infoValue}>
                  {format(new Date(estimations.estimatedEnd), "dd/MM/yyyy HH:mm", {
                    locale: es
                  })}
                </Typography>
              </Box>
            )}

            {estimations.currentProgress && (
              <Box className={classes.progressContainer}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" className={classes.infoLabel}>
                    Progreso: {estimations.currentProgress.sent} /{" "}
                    {estimations.currentProgress.total}
                  </Typography>
                  <Typography variant="body2" className={classes.infoValue}>
                    {Math.round(
                      (estimations.currentProgress.sent /
                        estimations.currentProgress.total) *
                        100
                    )}
                    %
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(
                    100,
                    (estimations.currentProgress.sent /
                      estimations.currentProgress.total) *
                      100
                  )}
                  className={classes.progressBar}
                />
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography variant="caption" color="textSecondary">
                    Transcurrido: {formatDuration(estimations.currentProgress.elapsed)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Restante: {formatDuration(estimations.currentProgress.remaining)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      )}

      {!selectedCampaignId && !loading && (
        <Typography variant="body2" color="textSecondary" align="center">
          Seleccione una campaña para ver las estimaciones de envío
        </Typography>
      )}
    </Paper>
  );
};

export default EstimationsPanel;

